/* Advent see-through pages (layout/sl-advent.liquid). Loaded once, deferred, from the layout head; works by event
   delegation and re-initialises on the theme editor's section events, so nothing depends on a script inside
   section markup.
   - Header: sl-header--clear while the hero ([data-sl-hero]) is under it, sl-header--solid once its sentinel
     ([data-sl-hero-end], one header height above the hero's foot) has scrolled above the viewport (W14).
   - Menu: the toggle opens the <dialog> modally; Escape, the close button or the backdrop close it and focus returns.
   - Hero video: after the load event, unless reduced motion or Save-Data is asked for; muted, inline, once (Joey,
     20 Oct): it holds its last frame and never restarts; fades in when it plays, pauses off screen. Larger screens take the smallest mp4 rendition that covers the hero; phones
     the same video's smallest rendition (founders, 19 Oct), or the phone video when one is set.
   - Card flip: a toggle button (aria-pressed); the CSS turns the card. The first card starts revealed on every
     screen (Matt, 20 Oct), as the hint that the cards flip.
   - Country/region selector (snippets/sl-localization.liquid, Q01): a <details> that works without script; this adds
     Escape (closes it, focus back on its button), arrow keys through the countries, closing on a click or focus
     outside, and the search field above 9 countries (shown only when this script runs).
   - Swipe rows: the dots under a phone row follow its scroll position; dot buttons (card quality) show their slide. A tile (any focusable inside it) that takes
     keyboard focus while it only peeks into its row scrolls whole into view, instantly: a smooth scroll on a snapping
     row does not move in WebKit (W10). Moved here from an inline script in sections/sl-shop-group.liquid (A24); it
     serves every swipe row (Shop groups, "Looking for something else?"). Rows that do not overflow are left alone. */
(() => {
  const CLEAR = 'sl-header--clear';
  const SOLID = 'sl-header--solid';
  let headerObserver;

  function initHeader() {
    const header = document.querySelector('[data-sl-header]');
    if (headerObserver) headerObserver.disconnect();
    if (!header || header.dataset.slTransparent === 'false') return;
    if (!header.classList.contains(CLEAR) && !header.dataset.slTransparent) return;
    header.dataset.slTransparent = 'true';
    const set = (solid) => {
      header.classList.toggle(SOLID, solid);
      header.classList.toggle(CLEAR, !solid);
    };
    const hero = document.querySelector('[data-sl-hero]');
    if (!hero) return set(true);
    // Nothing is measured up front (W14: WebKit could run this before the CSS applied, read an 867 px header and stay
    // solid for good). The CSS places the sentinel one header height above the hero's foot; the observer follows its
    // live position, so a late stylesheet only moves the sentinel. Solid once it has gone above the viewport.
    let sentinel = hero.querySelector('[data-sl-hero-end]');
    if (!sentinel) {
      sentinel = document.createElement('span');
      sentinel.className = 'sl-hero__sentinel';
      sentinel.setAttribute('data-sl-hero-end', '');
      sentinel.setAttribute('aria-hidden', 'true');
      hero.append(sentinel);
    }
    headerObserver = new IntersectionObserver(([entry]) => {
      set(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0));
    });
    headerObserver.observe(sentinel);
  }

  function initMenu() {
    const dialog = document.getElementById('sl-menu');
    if (!dialog || dialog.dataset.slReady) return;
    dialog.dataset.slReady = 'true';
    dialog.addEventListener('close', () => {
      const toggle = document.querySelector('[aria-controls="sl-menu"]');
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    });
  }

  function initVideo() {
    const video = document.querySelector('[data-sl-hero-video]');
    if (!video || video.dataset.slReady) return;
    video.dataset.slReady = 'true';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    if (reduce || saveData) return;
    const hero = video.closest('[data-sl-hero]');
    const start = () => {
      // Phones play the same video as larger screens (founders, 19 Oct). A portrait screen takes the portrait cut when
      // one is set (the centre of the same video, what object-fit: cover shows there anyway; Lighthouse dev-2: 1.25 MB
      // instead of 11 MB), a landscape screen the landscape video. Every screen takes the smallest rendition that
      // fills the hero at its pixel density, so a phone never stretches a 480 px file over a taller hero.
      const portrait = window.matchMedia('(orientation: portrait)').matches;
      const sources = [...video.querySelectorAll('source[data-src]')];
      const of = (o) => sources.filter((s) => s.dataset.orientation === o);
      const pool = portrait && of('portrait').length ? of('portrait') : of('landscape').length ? of('landscape') : of('portrait');
      if (!pool.length) return;
      pool.sort((a, b) => Number(a.dataset.height) - Number(b.dataset.height));
      const need = Math.min(1080, hero.getBoundingClientRect().height * (window.devicePixelRatio || 1));
      const pick = pool.find((s) => Number(s.dataset.height) >= need) || pool[pool.length - 1];
      video.muted = true;
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      video.dataset.slSource = pick.dataset.orientation;
      video.src = pick.dataset.src;
      let ended = false;
      video.addEventListener('ended', () => { ended = true; }, { once: true });
      const play = () => (ended ? undefined : video.play().catch(() => {}));
      play();
      new IntersectionObserver(([entry]) => (entry.isIntersecting ? play() : video.pause())).observe(hero);
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function initDots(root = document) {
    root.querySelectorAll('[data-sl-dots]').forEach((dots) => {
      const row = dots.previousElementSibling;
      if (!row || row.dataset.slDots) return;
      row.dataset.slDots = 'true';
      const marks = [...dots.children];
      let frame = 0;
      row.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const item = row.firstElementChild;
          if (!item) return;
          const step = item.getBoundingClientRect().width || 1;
          const max = row.scrollWidth - row.clientWidth;
          const index = row.scrollLeft >= max - 2 ? marks.length - 1 : Math.round(row.scrollLeft / step);
          marks.forEach((m, i) => {
            m.classList.toggle('is-active', i === index);
            if (!m.matches('button')) return;
            if (i === index) m.setAttribute('aria-current', 'true');
            else m.removeAttribute('aria-current');
          });
        });
      }, { passive: true });
    });
  }

  // Country/region selector.
  const openLocales = () => document.querySelectorAll('details.sl-locale[open]');
  const fold = (text) => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  function initLocale(root = document) {
    root.querySelectorAll('[data-sl-locale-search]').forEach((input) => { input.hidden = false; });
  }
  document.addEventListener('keydown', (event) => {
    const box = event.target.closest?.('details.sl-locale[open]');
    if (!box) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      box.open = false;
      box.querySelector('summary').focus();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = [...box.querySelectorAll('.sl-locale__option')].filter((b) => !b.closest('li').hidden);
    if (!items.length) return;
    event.preventDefault();
    const at = items.indexOf(document.activeElement);
    const next = event.key === 'ArrowDown' ? (at + 1) % items.length : (at <= 0 ? items.length : at) - 1;
    items[next].focus();
  });
  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('[data-sl-locale-search]')) return;
    const query = fold(event.target.value.trim());
    event.target.form.querySelectorAll('.sl-locale__option').forEach((option) => {
      option.closest('li').hidden = Boolean(query) && !fold(option.firstElementChild.textContent).includes(query);
    });
  });
  document.addEventListener('pointerdown', (event) => {
    openLocales().forEach((box) => { if (!box.contains(event.target)) box.open = false; });
  });
  // Focus leaving for another control closes the list. Focus moving to an element that holds the list does not: in Safari
  // a tapped button takes no focus, so focus falls back to the menu dialog, and closing then made the tap land on the
  // dialog (Joey 22 Oct: the phone menu's country list did nothing).
  document.addEventListener('focusout', (event) => {
    const box = event.target.closest?.('details.sl-locale[open]');
    const next = event.relatedTarget;
    if (box && next && !box.contains(next) && !next.contains(box)) box.open = false;
  });
  // A country posts the localization form and Shopify reloads the page, which takes a few seconds: show that at once
  // (Joey 22 Oct: "a long lag"). The list closes and the selector dims with a busy cursor until the new page loads.
  document.addEventListener('submit', (event) => {
    const box = event.target.closest?.('details.sl-locale');
    if (!box) return;
    box.open = false;
    box.setAttribute('aria-busy', 'true');
  });

  document.addEventListener('focusin', (event) => {
    const tile = event.target.closest('.sl-shop-grid > li, .sl-row > li');
    const row = tile?.parentElement;
    if (!row || row.scrollWidth <= row.clientWidth) return;
    const t = tile.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    if (t.left >= r.left - 1 && t.right <= r.right + 1) return;
    const pad = parseFloat(getComputedStyle(row).scrollPaddingLeft) || 0;
    row.scrollLeft += t.left - r.left - pad; // instant: no sl- row sets scroll-behavior
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[aria-controls="sl-menu"]');
    if (toggle) {
      const dialog = document.getElementById('sl-menu');
      if (dialog && !dialog.open) {
        dialog.showModal();
        toggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }
    const dialog = event.target.closest('dialog.sl-menu');
    if (dialog) {
      const r = dialog.getBoundingClientRect();
      const outside = event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom);
      if (outside || event.target.closest('[data-sl-menu-close]')) dialog.close();
      // A tapped link shows it is loading at once: the next page can take a few seconds, and a menu that did not react
      // read as a delay (Joey 22 Oct, M2).
      const link = event.target.closest('.sl-menu__link, .sl-menu__sublink');
      if (link && !event.defaultPrevented && !event.metaKey && !event.ctrlKey) link.setAttribute('aria-busy', 'true');
      return;
    }
    // Dot buttons (card quality, H12): show that slide. Instant, as a smooth scroll on a snapping row does not move in
    // WebKit (W10); the scroll handler above moves the active dot.
    const dot = event.target.closest('[data-sl-dot]');
    if (dot) {
      const row = dot.parentElement.previousElementSibling;
      const slide = row?.children[Number(dot.dataset.slDot)];
      if (slide) row.scrollLeft += slide.getBoundingClientRect().left - row.getBoundingClientRect().left - (parseFloat(getComputedStyle(row).scrollPaddingLeft) || 0);
      return;
    }
    const card = event.target.closest('[data-sl-flip]');
    if (card) card.setAttribute('aria-pressed', card.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
  });

  // Mouse drag on the card-quality slider (Joey 22 Oct, H3): touch and trackpads already scroll it. While dragging, snapping
  // is off and the row follows the pointer; on release it settles on the nearest slide (instantly: a smooth scroll on a
  // snapping row does not move in WebKit, W10), with a flick of 40px enough to move one slide on. Images do not drag.
  function initDrag(root = document) {
    root.querySelectorAll('.sl-quality__slides--many').forEach((row) => {
      if (row.dataset.slDrag) return;
      row.dataset.slDrag = 'true';
      let startX = 0;
      let startLeft = 0;
      let active = false;
      row.addEventListener('dragstart', (event) => event.preventDefault());
      row.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        active = true;
        startX = event.clientX;
        startLeft = row.scrollLeft;
        row.classList.add('is-dragging');
        row.setPointerCapture(event.pointerId);
      });
      row.addEventListener('pointermove', (event) => {
        if (active) row.scrollLeft = startLeft - (event.clientX - startX);
      });
      const end = (event) => {
        if (!active) return;
        active = false;
        const step = row.clientWidth || 1;
        const moved = startX - event.clientX;
        let index = Math.round(startLeft / step);
        if (moved > 40) index += 1;
        else if (moved < -40) index -= 1;
        index = Math.max(0, Math.min(row.children.length - 1, index));
        row.classList.remove('is-dragging');
        row.scrollLeft = index * step;
      };
      row.addEventListener('pointerup', end);
      row.addEventListener('pointercancel', end);
    });
  }

  const init = () => {
    initHeader();
    initMenu();
    initVideo();
    initDots();
    initLocale();
    initDrag();
  };
  init();
  document.addEventListener('shopify:section:load', init);
})();
